import { motion } from 'framer-motion';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Icon } from '../header/draft/Icon';
import { ModalContext } from '../../contexts/ModalContext';
import './play-all-queue-modal.css';

interface IEditorPlayListModal {
  /** 현재 재생목록 위에 추가하기 */
  onClickAddPlayAll: () => void;
  /** 현재 재생목록 비우고 추가하기 */
  onCLickResetPlayAll: () => void;
}

/** 전체 듣기 — 재생목록 적용 방식 선택 (Draft 글래스 모달) */
export default function EditorPlayListModal(props: IEditorPlayListModal) {
  const { t } = useTranslation();
  const { handleModal } = useContext(ModalContext);
  const { onClickAddPlayAll, onCLickResetPlayAll } = props;

  const close = () => handleModal();

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const onAdd = () => {
    onClickAddPlayAll();
    close();
  };

  const onReplace = () => {
    onCLickResetPlayAll();
    close();
  };

  return createPortal(
    <div
      className="play-all-queue-modal-root fixed inset-0 z-[10001] flex items-end sm:items-center justify-center p-0 sm:p-4 lg:p-6"
      role="presentation"
    >
      <motion.button
        type="button"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="play-all-queue-modal-root__backdrop"
        aria-label={t('playAllQueueModal.cancel')}
        onClick={close}
      />

      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 360, damping: 32 }}
        className="play-all-queue-modal-root__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="play-all-queue-modal-title"
      >
        <header className="flex items-center mb-1">
          <div className="w-9 shrink-0" aria-hidden />
          <h2 id="play-all-queue-modal-title" className="play-all-queue-modal-root__title">
            {t('playAllQueueModal.title')}
          </h2>
          <button
            type="button"
            onClick={close}
            className="play-all-queue-modal-root__close"
            aria-label={t('playAllQueueModal.cancel')}
          >
            <Icon name="close" className="text-2xl" />
          </button>
        </header>

        <p className="play-all-queue-modal-root__subtitle">{t('playAllQueueModal.subtitle')}</p>

        <div className="play-all-queue-modal-root__actions">
          <button type="button" className="play-all-queue-modal-root__action" onClick={onAdd}>
            <span className="play-all-queue-modal-root__action-icon" aria-hidden>
              <Icon name="playlist_add" />
            </span>
            <span>{t('playAllQueueModal.addToCurrent')}</span>
          </button>
          <button
            type="button"
            className="play-all-queue-modal-root__action play-all-queue-modal-root__action--replace"
            onClick={onReplace}
          >
            <span className="play-all-queue-modal-root__action-icon" aria-hidden>
              <Icon name="playlist_play" />
            </span>
            <span>{t('playAllQueueModal.replaceCurrent')}</span>
          </button>
        </div>

        <footer className="play-all-queue-modal-root__footer">
          <button type="button" onClick={close} className="play-all-queue-modal-root__btn-cancel">
            {t('playAllQueueModal.cancel')}
          </button>
        </footer>
      </motion.div>
    </div>,
    document.body,
  );
}
